import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    FaDatabase,
    FaMoneyCheckAlt,
    FaCalendarCheck,
    FaHandHoldingUsd,
    FaHandshake,
    FaWallet,
    FaFileInvoice,
    FaBolt,
    FaBookmark,
    FaTimes,
    FaSave,
} from "react-icons/fa";
import { getDataToolsDatasets, runDataToolsQuery } from "../../Api/Api";
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";
import QueryBuilder from "./QueryBuilder";
import ResultsPanel from "./ResultsPanel";
import {
    PRESETS,
    describeQuery,
    emptyQuery,
    loadSavedViews,
    presetQuery,
    storeSavedViews,
    toRequest,
    validateQuery,
} from "./dataToolsUtils";

const DATASET_ICONS = {
    loans: FaMoneyCheckAlt,
    emis: FaCalendarCheck,
    emiPayments: FaHandHoldingUsd,
    settlements: FaHandshake,
    expenses: FaWallet,
    bills: FaFileInvoice,
};

const DataToolsPage = () => {
    const { workshopId, loanEnabled, workshopEnabled } = useWorkshop();
    const { showNotification } = useNotification();

    const [meta, setMeta] = useState(null);
    const [metaError, setMetaError] = useState(null);
    const [datasetKey, setDatasetKey] = useState(null);
    const [query, setQuery] = useState(null);

    const [result, setResult] = useState(null);
    const [resultInfo, setResultInfo] = useState({ mode: "LIST", description: "" });
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(false);

    const [savedViews, setSavedViews] = useState([]);
    const [saveName, setSaveName] = useState(null); // null = save box closed

    // Only offer datasets for the modules this workshop uses.
    const datasets = useMemo(() => {
        if (!meta) return [];
        return meta.datasets.filter((d) =>
            d.module === "COMMON" || (d.module === "LOAN" && loanEnabled) || (d.module === "WORKSHOP" && workshopEnabled));
    }, [meta, loanEnabled, workshopEnabled]);

    const dataset = datasets.find((d) => d.key === datasetKey);
    const operators = useMemo(() => meta?.operators || {}, [meta]);
    const presets = PRESETS.filter((p) => datasets.some((d) => d.key === p.dataset));

    const run = useCallback(async (ds, q) => {
        if (!workshopId) {
            setError("We couldn't tell which workshop you're logged in to. Please log out and log in again.");
            return;
        }
        const problem = validateQuery(ds, q, operators);
        if (problem) {
            setError(null);
            showNotification(problem, "error");
            return;
        }
        setRunning(true);
        setError(null);
        try {
            const res = await runDataToolsQuery(toRequest(workshopId, ds.key, q));
            setResult(res.data);
            setResultInfo({ mode: q.mode, description: describeQuery(ds, q, operators), datasetKey: ds.key });
            setStale(false);
        } catch (err) {
            setResult(null);
            setError(err.response?.data?.message || "Something went wrong while getting your data. Please try again.");
        } finally {
            setRunning(false);
        }
    }, [workshopId, operators, showNotification]);

    useEffect(() => {
        getDataToolsDatasets()
            .then((res) => setMeta(res.data))
            .catch(() => setMetaError("Data Tools couldn't load. Please refresh the page."));
    }, []);

    // Logged in to a different workshop: drop everything from the previous one.
    useEffect(() => {
        setDatasetKey(null);
        setQuery(null);
        setResult(null);
        setError(null);
        setSavedViews(workshopId ? loadSavedViews(workshopId) : []);
    }, [workshopId]);

    // Open the first dataset straight away so the page never starts empty.
    useEffect(() => {
        if (!datasetKey && datasets.length > 0 && workshopId) {
            const first = datasets[0];
            const q = emptyQuery(first);
            setDatasetKey(first.key);
            setQuery(q);
            run(first, q);
        }
    }, [datasets, datasetKey, workshopId, run]);

    const selectDataset = (ds, q = emptyQuery(ds)) => {
        setDatasetKey(ds.key);
        setQuery(q);
        setSaveName(null);
        run(ds, q);
    };

    const changeQuery = (next) => {
        setQuery(next);
        if (result) setStale(true);
    };

    const openPreset = (preset) => {
        const ds = datasets.find((d) => d.key === preset.dataset);
        selectDataset(ds, presetQuery(ds, preset));
    };

    const saveView = () => {
        const name = (saveName || "").trim();
        if (!name) return;
        const views = [...savedViews.filter((v) => v.name !== name), { name, dataset: datasetKey, query }];
        setSavedViews(views);
        storeSavedViews(workshopId, views);
        setSaveName(null);
        showNotification(`Saved “${name}”`, "success");
    };

    const openView = (view) => {
        const ds = datasets.find((d) => d.key === view.dataset);
        if (ds) selectDataset(ds, { ...emptyQuery(ds), ...view.query });
    };

    const deleteView = (name) => {
        const views = savedViews.filter((v) => v.name !== name);
        setSavedViews(views);
        storeSavedViews(workshopId, views);
    };

    if (metaError) {
        return <div className="p-8 text-red-600">{metaError}</div>;
    }

    if (!workshopId) {
        return (
            <div className="p-8 text-red-600">
                We couldn't tell which workshop you're logged in to. Please log out and log in again to use Data Tools.
            </div>
        );
    }

    const resultDataset = datasets.find((d) => d.key === resultInfo.datasetKey) || dataset;
    const today = new Date().toISOString().slice(0, 10);

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
            <div className="max-w-screen-2xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <FaDatabase className="text-teal-600" /> Data Tools
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Explore your financial data without any technical knowledge. Pick a topic, add conditions, and get a list or a summary.
                        Nothing here can change your data — it's look-only.
                    </p>
                </div>

                {/* Topic picker */}
                <div>
                    <h2 className="text-sm font-semibold text-gray-600 mb-2">What do you want to look at?</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        {!meta && [...Array(6)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}
                        {datasets.map((d) => {
                            const Icon = DATASET_ICONS[d.key] || FaDatabase;
                            const active = d.key === datasetKey;
                            return (
                                <button
                                    key={d.key}
                                    onClick={() => selectDataset(d)}
                                    className={`text-left p-4 rounded-xl border-2 transition bg-white ${
                                        active ? "border-teal-500 shadow-md" : "border-gray-100 shadow-sm hover:border-teal-200"
                                    }`}
                                    title={d.description}
                                >
                                    <div className={`inline-flex p-2 rounded-lg mb-2 ${active ? "bg-teal-600 text-white" : "bg-teal-50 text-teal-600"}`}>
                                        <Icon />
                                    </div>
                                    <div className="font-semibold text-gray-800 text-sm">{d.label}</div>
                                    <div className="text-xs text-gray-500 mt-0.5 max-h-8 overflow-hidden">{d.description}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Quick questions + saved views */}
                {(presets.length > 0 || savedViews.length > 0) && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
                        {presets.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-gray-600 flex items-center gap-1 mr-1">
                                    <FaBolt className="text-yellow-500" /> Quick questions:
                                </span>
                                {presets.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => openPreset(p)}
                                        title={p.hint}
                                        className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-800 transition"
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {savedViews.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-gray-600 flex items-center gap-1 mr-1">
                                    <FaBookmark className="text-teal-600" /> My saved views:
                                </span>
                                {savedViews.map((v) => (
                                    <span key={v.name} className="inline-flex items-center rounded-full bg-teal-50 text-teal-800 text-sm">
                                        <button onClick={() => openView(v)} className="pl-3 pr-1 py-1.5 hover:underline">{v.name}</button>
                                        <button onClick={() => deleteView(v.name)} className="pr-2 pl-1 py-1.5 text-teal-500 hover:text-red-600" title="Delete saved view">
                                            <FaTimes size={10} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {dataset && query && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        <div className="lg:col-span-5 xl:col-span-4 space-y-3">
                            <QueryBuilder
                                dataset={dataset}
                                operators={operators}
                                query={query}
                                onChange={changeQuery}
                                onRun={() => run(dataset, query)}
                                onReset={() => selectDataset(dataset)}
                                running={running}
                                workshopId={workshopId}
                            />
                            {saveName === null ? (
                                <button
                                    onClick={() => setSaveName("")}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 text-sm text-teal-700 bg-white border border-gray-200 rounded-lg hover:bg-teal-50 transition"
                                >
                                    <FaSave className="mr-2" /> Save this view for later
                                </button>
                            ) : (
                                <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                                    <input
                                        autoFocus
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Give it a name, e.g. Monthly collections"
                                        value={saveName}
                                        onChange={(e) => setSaveName(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && saveView()}
                                    />
                                    <div className="flex gap-2">
                                        <button onClick={saveView} className="flex-1 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700">Save</button>
                                        <button onClick={() => setSaveName(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                                    </div>
                                    <p className="text-xs text-gray-400">Saved views are kept in this browser.</p>
                                </div>
                            )}
                        </div>
                        <div className="lg:col-span-7 xl:col-span-8 min-w-0">
                            <ResultsPanel
                                dataset={resultDataset}
                                result={result}
                                mode={resultInfo.mode}
                                description={resultInfo.description}
                                loading={running}
                                error={error}
                                stale={stale}
                                fileName={`${resultDataset?.label || "data"} ${today}.csv`}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataToolsPage;
